<template>
  <div class="text-left gestionnaire-page">
    <BreadcrumbsNav />

    <!-- Hero -->
    <section class="hero-section">
      <v-row align="center">
        <v-col cols="12" md="8">
          <div class="hero-badge">
            Mairies · Services de l'enfance · Gestionnaires de cantine · Directeur de restauration collective
          </div>
          <h1 class="hero-title">
            Pilotez votre restauration collective.
            <br />
            <span class="hero-title--accent">Fluidifiez. Mesurez. Innovez.</span>
          </h1>
          <p class="hero-desc">
            Labricot dans ma cantine centralise toutes les informations de votre service — absences, feedbacks, surplus,
            conformité EGAlim — dans un outil pensé pour libérer vos équipes, pas pour les surcharger.
          </p>
          <div class="hero-stats">
            <div class="stat-chip">
              <v-img
                src="/static/images/etincelle_gouv.png"
                contain
                max-height="36px"
                max-width="100px"
                class="mx-auto"
              ></v-img>
              <div class="text-caption grey--text">Lauréat 2024</div>
            </div>
            <div class="stat-chip">
              <span class="stat-chip__num">24 ans</span>
              <span class="stat-chip__label">d'expérience</span>
            </div>
            <div class="stat-chip">
              <span class="stat-chip__num">−12%</span>
              <span class="stat-chip__label">gaspillage constaté</span>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="4" class="d-none d-md-flex justify-center">
          <v-img src="/static/images/doodles-dsfr/primary/ReadingDoodle.png" contain max-width="260" />
        </v-col>
      </v-row>
    </section>

    <!-- Problèmes résolus -->
    <section class="section-problems">
      <h2 class="section-title">Quand la gestion devient une charge…</h2>
      <p class="section-subtitle">4 problèmes que nos clients rencontraient avant Labricot</p>
      <v-row>
        <v-col cols="12" sm="6" v-for="problem in problems" :key="problem.title">
          <div class="problem-card">
            <span class="problem-card__icon">{{ problem.icon }}</span>
            <div>
              <p class="problem-card__title">{{ problem.title }}</p>
              <p class="problem-card__desc">{{ problem.desc }}</p>
            </div>
          </div>
        </v-col>
      </v-row>
    </section>

    <!-- Cas 1 : Digitalisation -->
    <section class="section-usecase">
      <div class="usecase-label">Cas d'usage #1</div>
      <h2 class="section-title">Digitaliser les remontées d'informations</h2>
      <p class="section-subtitle">
        Un circuit simple entre l'école, la cantine et la cuisine centrale — sans papier, sans ressaisie.
      </p>

      <v-row class="mt-8" align="stretch">
        <v-col cols="12" md="4" v-for="actor in actorsCas1" :key="actor.role">
          <div class="actor-card">
            <div class="actor-card__num">{{ actor.num }}</div>
            <div class="actor-card__role">{{ actor.role }}</div>
            <ul class="actor-card__actions">
              <li v-for="action in actor.actions" :key="action">{{ action }}</li>
            </ul>
            <div class="actor-card__benefit" v-if="actor.benefit">
              <span class="benefit-label">Bénéfice</span>
              {{ actor.benefit }}
            </div>
          </div>
        </v-col>
      </v-row>
    </section>

    <!-- Cas 2 : Gaspillage & absentéisme -->
    <section class="section-usecase section-usecase--alt">
      <div class="usecase-label">Cas d'usage #2</div>
      <h2 class="section-title">Gérer le gaspillage lié à l'absentéisme</h2>
      <p class="section-subtitle">
        Des outils concrets pour adapter les quantités, valoriser les surplus et réduire les coûts.
      </p>

      <v-row class="mt-8" align="stretch">
        <v-col cols="12" md="4" v-for="actor in actorsCas2" :key="actor.role">
          <div class="actor-card actor-card--green">
            <div class="actor-card__num actor-card__num--green">{{ actor.num }}</div>
            <div class="actor-card__role">{{ actor.role }}</div>
            <ul class="actor-card__actions">
              <li v-for="action in actor.actions" :key="action">{{ action }}</li>
            </ul>
            <div class="actor-card__benefit" v-if="actor.benefit">
              <span class="benefit-label">Bénéfice</span>
              {{ actor.benefit }}
            </div>
          </div>
        </v-col>
      </v-row>
    </section>

    <!-- Services outillés -->
    <section class="section-services">
      <h2 class="section-title">Ce que nos services font pour vous</h2>
      <v-row class="mt-6">
        <v-col cols="12" sm="6" md="4" v-for="service in services" :key="service.title">
          <div class="service-card">
            <div class="service-card__icon">{{ service.icon }}</div>
            <h3 class="service-card__title">{{ service.title }}</h3>
            <p class="service-card__desc">{{ service.desc }}</p>
            <div class="service-card__benefit">
              <strong>→</strong>
              {{ service.benefit }}
            </div>
          </div>
        </v-col>
      </v-row>
    </section>

    <!-- CTA -->
    <section class="section-cta">
      <h2 class="cta-title">Prêt à fluidifier votre gestion ?</h2>
      <p class="cta-desc">Rejoignez les établissements qui pilotent enfin leur restauration avec Labricot.</p>
      <div class="cta-actions">
        <v-btn color="primary" large class="cta-btn mr-4" href="mailto:contact@labsoft.fr">
          Demander une démo
        </v-btn>
        <v-btn outlined large class="cta-btn" :to="{ name: 'NutritionistePage2' }">
          Espace Nutrition →
        </v-btn>
      </div>
    </section>
  </div>
</template>

<script>
import BreadcrumbsNav from "@/components/BreadcrumbsNav"

export default {
  name: "GestionnairePage",
  components: { BreadcrumbsNav },
  data() {
    return {
      problems: [
        {
          icon: "📋",
          title: "Manque de canaux de communication",
          desc: "L'info sur les absences ne redescend pas et trop d'aliments préparés sont jetés.",
        },
        {
          icon: "🍽️",
          title: "Pas de retours sur les recettes",
          desc: "Les plats peu appréciés continuent d'être livrés, générant du gaspillage systématique.",
        },
        {
          icon: "🌡️",
          title: "Menus inadaptés au contexte",
          desc: "Les enfants ont faim mais manquent d'appétit face à des plats peu adaptés à la météo ou à la saison.",
        },
        {
          icon: "⚖️",
          title: "Quantités estimées à l'aveugle",
          desc: "Sans données historiques, les agents servent trop par précaution — et le surplus finit à la poubelle.",
        },
      ],
      actorsCas1: [
        {
          num: "1",
          role: "Élève",
          actions: ["Donner son avis sur le repas du jour en 2 clics", "Préciser la raison s'il n'a pas assez mangé"],
          benefit: null,
        },
        {
          num: "2",
          role: "Animateur",
          actions: [
            "Remonter l'appréciation globale et détaillée",
            "Afficher le gaspillage de manière ludique",
            "Transmettre automatiquement les retours à la cuisine centrale",
          ],
          benefit: "Moins de repas en trop préparés, meilleure adéquation livraisons / besoins réels.",
        },
        {
          num: "3",
          role: "Cuisine centrale",
          actions: [
            "Recevoir les feedbacks plat par plat en temps réel",
            "Ajuster les recettes et les grammages sur la base de données terrain",
          ],
          benefit: "Dialogue apaisé et factuel basé sur des données, pas des impressions.",
        },
      ],
      actorsCas2: [
        {
          num: "1",
          role: "Référent scolaire",
          actions: [
            "Renseigner les absences du jour par niveau",
            "Consulter les infos sur le gaspillage alimentaire",
            "Apprécier les retours envoyés automatiquement au prestataire",
          ],
          benefit: null,
        },
        {
          num: "2",
          role: "Chef de salle",
          actions: [
            "Consulter les absences pour mettre en chauffe le bon nombre de barquettes",
            "Signaler les excédents pour informer les associations",
            "Mesurer le gaspillage par déclaration simple",
          ],
          benefit: "Moins de restes dans les bacs, sans rationner les enfants.",
        },
        {
          num: "3",
          role: "Animateur",
          actions: [
            "Remonter l'appréciation détaillée par plat",
            "Afficher le gaspillage de manière ludique aux enfants",
            "Renseigner le contexte (météo, bruit, ambiance)",
          ],
          benefit: "Les enfants deviennent acteurs de la réduction du gaspillage.",
        },
      ],
      services: [
        {
          icon: "📡",
          title: "Communication centralisée",
          desc:
            "Absences, spécificités de groupe et retours sur les plats dans un outil partagé entre l'école, la cuisine et les équipes de service.",
          benefit: "Dialogue factuel, décisions éclairées.",
        },
        {
          icon: "♻️",
          title: "Valorisation des surplus",
          desc:
            "Circuit de don adapté aux associations et banques alimentaires, dans le respect des bonnes pratiques d'hygiène.",
          benefit: "Baisse des coûts de collecte et valorisation de l'image.",
        },
        {
          icon: "📊",
          title: "Conformité EGAlim",
          desc:
            "Tableaux de bord lisibles pour la direction, les élus et la cuisine centrale. Aide à l'alimentation de macantine.gouv.fr.",
          benefit: "Vous démontrez vos efforts, chiffres à l'appui.",
        },
        {
          icon: "🎯",
          title: "Calibrage des portions",
          desc: "Portions ajustées à partir de l'historique de consommation observée, et non « à l'aveugle ».",
          benefit: "Moins de restes, sans sentiment de rationner.",
        },
        {
          icon: "🧑‍🍳",
          title: "Menus adaptés au terrain",
          desc: "Collecte d'indicateurs sur ce qui est consommé ou jeté, par plat, par météo, par contexte.",
          benefit: "Diminution du gaspillage par ajustement progressif.",
        },
        {
          icon: "🏫",
          title: "Projet pédagogique partagé",
          desc:
            "Cadre pédagogique commun autour du « bien-manger », capitalisé et duplicable à l'ensemble du réseau de cantines.",
          benefit: "Des ressources réutilisables d'années en années.",
        },
      ],
    }
  },
}
</script>

<style scoped>
.gestionnaire-page {
  font-family: "Marianne", "Segoe UI", sans-serif;
}

/* Hero */
.hero-section {
  background: linear-gradient(135deg, #fff8f5 0%, #fff3ee 60%, #fde8da 100%);
  border-radius: 20px;
  padding: 48px 40px;
  margin-bottom: 56px;
  border: 1px solid #f4c5b0;
}

.hero-badge {
  display: inline-block;
  background: #fde8da;
  color: #c94b1a;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 20px;
  margin-bottom: 20px;
}

.hero-title {
  font-size: clamp(1.6rem, 4vw, 2.2rem);
  font-weight: 900;
  line-height: 1.25;
  color: #1a1a2e;
  margin-bottom: 16px;
}

.hero-title--accent {
  color: #e05a2b;
}

.hero-desc {
  font-size: 1rem;
  line-height: 1.7;
  color: #444;
  max-width: 560px;
  margin-bottom: 28px;
}

.hero-stats {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.stat-chip {
  background: white;
  border: 1px solid #f4c5b0;
  border-radius: 12px;
  padding: 10px 18px;
  text-align: center;
}

.stat-chip__num {
  display: block;
  font-size: 1.4rem;
  font-weight: 900;
  color: #e05a2b;
}

.stat-chip__label {
  font-size: 0.72rem;
  color: #777;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Problems */
.section-problems {
  margin-bottom: 64px;
}

.problem-card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  background: #fafafa;
  border: 1px solid #f0e8e0;
  border-left: 4px solid #e05a2b;
  border-radius: 10px;
  padding: 18px;
  height: 100%;
}

.problem-card__icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.problem-card__title {
  font-weight: 700;
  font-size: 0.9rem;
  color: #1a1a2e;
  margin-bottom: 4px;
}

.problem-card__desc {
  font-size: 0.825rem;
  color: #666;
  line-height: 1.5;
  margin: 0;
}

/* Use cases */
.section-usecase {
  margin-bottom: 64px;
}

.section-usecase--alt {
  background: #f3fbf6;
  border-radius: 20px;
  padding: 40px;
}

.usecase-label {
  display: inline-block;
  background: #1a1a2e;
  color: white;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 4px;
  margin-bottom: 12px;
}

.section-title {
  font-size: clamp(1.3rem, 3vw, 1.7rem);
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 8px;
}

.section-subtitle {
  font-size: 0.95rem;
  color: #666;
  margin-bottom: 0;
}

/* Actor cards */
.actor-card {
  background: white;
  border: 1px solid #f4c5b0;
  border-top: 4px solid #e05a2b;
  border-radius: 12px;
  padding: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.actor-card--green {
  border-color: #a8d9bc;
  border-top-color: #2d9e5f;
}

.actor-card__num {
  width: 36px;
  height: 36px;
  background: #e05a2b;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 1rem;
  margin-bottom: 12px;
}

.actor-card__num--green {
  background: #2d9e5f;
}

.actor-card__role {
  font-weight: 800;
  font-size: 1.05rem;
  color: #1a1a2e;
  margin-bottom: 14px;
}

.actor-card__actions {
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
  flex: 1;
}

.actor-card__actions li {
  font-size: 0.84rem;
  color: #444;
  line-height: 1.5;
  padding: 5px 0;
  border-bottom: 1px dashed #eee;
}

.actor-card__actions li:last-child {
  border-bottom: none;
}

.actor-card__actions li::before {
  content: "→ ";
  color: #e05a2b;
  font-weight: 700;
}

.actor-card--green .actor-card__actions li::before {
  color: #2d9e5f;
}

.actor-card__benefit {
  background: #fdf6f3;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.8rem;
  color: #555;
  line-height: 1.5;
}

.actor-card--green .actor-card__benefit {
  background: #edf7f1;
}

.benefit-label {
  display: block;
  font-weight: 800;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #e05a2b;
  margin-bottom: 4px;
}

.actor-card--green .benefit-label {
  color: #2d9e5f;
}

/* Services */
.section-services {
  margin-bottom: 64px;
}

.service-card {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 14px;
  padding: 24px;
  height: 100%;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.service-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  transform: translateY(-3px);
}

.service-card__icon {
  font-size: 1.8rem;
  margin-bottom: 12px;
}

.service-card__title {
  font-weight: 800;
  font-size: 0.95rem;
  color: #1a1a2e;
  margin-bottom: 8px;
}

.service-card__desc {
  font-size: 0.84rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 12px;
}

.service-card__benefit {
  font-size: 0.8rem;
  color: #e05a2b;
  font-weight: 600;
}

/* CTA */
.section-cta {
  background: linear-gradient(135deg, #1a1a2e 0%, #2d2d4e 100%);
  border-radius: 20px;
  padding: 56px 40px;
  text-align: center;
  margin-bottom: 40px;
}

.cta-title {
  font-size: clamp(1.4rem, 3vw, 1.9rem);
  font-weight: 900;
  color: white;
  margin-bottom: 12px;
}

.cta-desc {
  color: rgba(255, 255, 255, 0.75);
  font-size: 1rem;
  margin-bottom: 28px;
}

.cta-btn {
  text-transform: none;
  font-weight: 700;
  border-radius: 10px;
}
</style>
